import { domToBlob } from "modern-screenshot";
import modernScreenshotWorkerUrl from "modern-screenshot/worker?url";
import { useCallback, useEffect, useState, type RefObject } from "react";

import {
  nodeSnapshotHandlerEventName,
  nodeSnapshotHandlerRequestEventName,
  publishEvent,
  useEventSubscribe,
} from "../events/index.ts";

// Need to do a little dance to ensure that the handler can be requested from a node after it is rendered.

export interface NodeSnapshotOptions {
  // Default to "image/webp"
  type?: string;
  // Image width and height in logical pixels.
  // Default to the node's clientWidth/clientHeight (e.g. 1x scale)
  width?: number;
  height?: number;
  // Default to 2x
  scaleFactor?: number;
}

export type NodeSnapshotHandler = (options?: NodeSnapshotOptions) => Promise<Blob>;

/**
 * Provides an implementation of a node snapshot handler using modern-screenshot library.
 */
export function useDefaultNodeSnapshotHandler<TElement extends HTMLElement>(
  ref: RefObject<TElement | null>,
): NodeSnapshotHandler {
  return useCallback(
    async ({ type = "image/webp", width, height, scaleFactor = 2 }: NodeSnapshotOptions = {}) => {
      if (!ref.current) {
        throw new Error("screenshot ref is not attached to an element.");
      }

      const { clientWidth, clientHeight } = ref.current;

      // modern-screenshot doesn't support setting element width and height separately to the output image size,
      // so we need to calculate a base scale factor to determine the output image size before applying the
      // requested scale factor.
      // If the user specified both width and height, and they don't match the element's aspect ratio,
      // using min() means the output image will be a "letterbox" fit inside the specified dimensions.
      // Strictly, using a fallback to 1
      let scale = scaleFactor;
      if (width || height) {
        scale *= Math.min(width ? width / clientWidth : Infinity, height ? height / clientHeight : Infinity);
      }

      // Paranoia, mostly, but modern-screenshot will just return a 0-byte Blob if the size is 0,
      // and too large images could cause other bad issues. In practice, this should only be hit if something
      // particularly weird is selected, so failing loudly seems reasonable.
      const resultWidth = Math.round(clientWidth * scale);
      const resultHeight = Math.round(clientHeight * scale);
      if (!((0 < resultWidth && resultWidth < 10_000) || (0 < resultHeight && resultHeight < 10_000))) {
        throw new Error(`Invalid snapshot size: ${resultWidth}x${resultHeight}`);
      }

      return await domToBlob(ref.current, {
        workerUrl: modernScreenshotWorkerUrl,
        type,
        width: clientWidth,
        height: clientHeight,
        scale,
      });
    },
    [ref],
  );
}

/**
 * Provide a "snapshot handler" for a node, which should return an image of the node as a blob.
 */
export function useProvideNodeSnapshotHandler(nodeId: number, handler: NodeSnapshotHandler) {
  // Stable callback to publish the handler, required by useEventSubscribe.
  const publishHandler = useCallback(() => {
    publishEvent(nodeSnapshotHandlerEventName(nodeId), handler);
  }, [nodeId, handler]);

  // Listen for requests for the handler after the node is rendered.
  useEventSubscribe(nodeSnapshotHandlerRequestEventName(nodeId), publishHandler);

  // Publish the initial state in case there is an existing useNodeSnapshotHandler waiting for it,
  // and also to update the handler if it happens to change (e.g. if it depends on props or state).
  useEffect(() => {
    publishHandler();
  }, [publishHandler]);
}

export function useNodeSnapshotHandler(nodeId: number): NodeSnapshotHandler | null {
  // Store the handler in state to be updated by useProvideNodeSnapshotHandler when it is published.
  const [state, setState] = useState<NodeSnapshotHandler | null>(null);
  // Annoyingly, we can't just use setState directly, because the handler is a function and setState will
  // call it assuming it's a state updater function.
  const handlerChanged = useCallback((handler: NodeSnapshotHandler) => {
    setState(() => handler);
  }, []);
  useEventSubscribe(nodeSnapshotHandlerEventName(nodeId), handlerChanged);

  // Request the initial handler state.
  useEffect(() => {
    publishEvent(nodeSnapshotHandlerRequestEventName(nodeId));
  }, [nodeId]);

  return state;
}
